import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import { appAlert } from '../../lib/dialogs';

// Build a short flag label + colour class from IDEXX's per-test fields.
// IDEXX uses `outOfRange: boolean` (no separate flag string). We derive H/L
// by comparing the numeric result to the low/high reference, and CH/CL when
// the value is past the critical thresholds. Returns null when the result
// is in range so the caller can skip the badge entirely.
function deriveTestFlag(test) {
    if (!test || !test.outOfRange) return null;
    const v = test.resultNumeric;
    const { low, high, criticalLow, criticalHigh } = test;
    if (typeof v === 'number') {
        if (typeof criticalLow === 'number' && v < criticalLow) return { code: 'CL', critical: true };
        if (typeof criticalHigh === 'number' && v > criticalHigh) return { code: 'CH', critical: true };
        if (typeof low === 'number' && v < low) return { code: 'L', critical: false };
        if (typeof high === 'number' && v > high) return { code: 'H', critical: false };
    }
    return { code: '!', critical: false };
}

// IDEXX returns interpretation/notes as HTML-ish strings — `<br/>` for line
// breaks, numeric entities like `&#60;` (=`<`) and `&#62;` (=`>`), and the
// usual named entities. Convert to plain text with real newlines so the UI
// can render with `white-space: pre-wrap` instead of escaping the markup.
function decodeIdexxText(raw) {
    if (raw == null) return '';
    let s = String(raw);
    // Normalise <br>, <br/>, <br />, <BR/>, etc. to a newline.
    s = s.replace(/<\s*br\s*\/?\s*>/gi, '\n');
    // Strip any other HTML tags defensively.
    s = s.replace(/<[^>]*>/g, '');
    // Decode numeric entities (&#60; / &#x3C;).
    s = s.replace(/&#(\d+);/g, (_, n) => String.fromCharCode(parseInt(n, 10)));
    s = s.replace(/&#x([0-9a-f]+);/gi, (_, n) => String.fromCharCode(parseInt(n, 16)));
    // Common named entities.
    const named = {
        amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', ndash: '–', mdash: '—',
    };
    s = s.replace(/&([a-z]+);/gi, (m, name) => (named[name.toLowerCase()] != null ? named[name.toLowerCase()] : m));
    return s;
}

@observer
class LabResultsListMini extends Component {
    constructor(props) {
        super(props);
        this.state = {
            labResults: [],
            loading: false,
            expandedResult: null,
        };
    }

    componentDidMount() {
        this.loadResults();
        // Background poll for new results every 60 s while the drawer is
        // open and the tab is visible. We don't have an "is anything still
        // pending?" signal here because results come from IDEXX-side
        // simulation, so we just refresh quietly. Stops on unmount.
        this._poll = setInterval(this.maybePoll, 60000);
        if (typeof window !== 'undefined') {
            this._onFocus = () => {
                if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
                    this.loadResults();
                }
            };
            window.addEventListener('focus', this._onFocus);
            if (typeof document !== 'undefined') {
                document.addEventListener('visibilitychange', this._onFocus);
            }
        }
    }

    componentWillUnmount() {
        if (this._poll) {
            clearInterval(this._poll);
            this._poll = null;
        }
        if (typeof window !== 'undefined' && this._onFocus) {
            window.removeEventListener('focus', this._onFocus);
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', this._onFocus);
            }
        }
    }

    maybePoll = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        if (this.state.loading) return;
        this.loadResults();
    }

    componentDidUpdate(prevProps) {
        const prevJournalId = prevProps.journal?.id || prevProps.journal || 0;
        const nowJournalId = this.props.journal?.id || this.props.journal || 0;
        if (prevProps.animal !== this.props.animal || prevJournalId !== nowJournalId) {
            if (this.props.animal || nowJournalId) this.loadResults();
        }
    }

    loadResults = async () => {
        // One result belongs to one journal — when we have a journal id,
        // scope the fetch to it so the section only shows results for THIS
        // journal. Falls back to animal scope only if no journal is provided
        // (e.g. an animal-detail page that's not journal-bound).
        const { animal, journal } = this.props;
        const journalId = journal?.id || (typeof journal === 'number' ? journal : 0);
        if (!journalId && !animal) return;

        this.setState({ loading: true });
        const url = journalId
            ? `/api/integrations/idexx/results/search?journal=${journalId}`
            : `/api/integrations/idexx/results/search?animal=${animal}`;
        const response = await util.fetchApi(url);
        const labResults = response?.data?.results || response?.data || [];
        this.setState({ labResults, loading: false });
    }

    onClickFetchNew = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.setState({ loading: true });
        const { idexxStore } = this.props.stores;
        await idexxStore.getLatestResults();
        const { latestBatchId } = idexxStore;
        if (latestBatchId) {
            await idexxStore.confirmResults(latestBatchId);
        }
        // Also re-pull the results we already hold straight from IDEXX.
        // In-house re-runs are folded into an existing result record, and a
        // batch that was already confirmed is never re-delivered by
        // /results/latest — this is the only way to recover such an update.
        // ?refresh=1 keeps the endpoint from stamping the result as viewed.
        const { labResults = [] } = this.state;
        await Promise.all(labResults
            .filter(result => result && result.id)
            .map(result => util.fetchApi(`/api/integrations/idexx/results/${result.id}?refresh=1`)
                .catch(() => null)));
        await this.loadResults();
        this.setState({ loading: false });
    }

    onClickToggleExpand = (e) => {
        e.stopPropagation();
        const { id } = e.target.closest('.lab-result').dataset;
        this.setState(prevState => ({
            expandedResult: prevState.expandedResult === id ? null : id,
        }));
    }

    onClickViewPdf = (e, result) => {
        e.stopPropagation();
        e.preventDefault();
        if (!result.hasPdf) return;
        const jwtToken = util.getJwtToken();
        const { idexxStore } = this.props.stores;
        const pdfUrl = idexxStore.getResultPdfUrl(result.id, jwtToken);
        const apiServer = util.getApiServer();
        window.open(`${apiServer}${pdfUrl}`, '_blank');
    }

    // Fetch a single-use authenticated URL into the VetConnect PLUS web UI
    // for this result. IDEXX issues a fresh token per call, so we open the
    // popup synchronously after the click and then redirect it once the
    // backend resolves — popping it later would trip Safari/Chrome popup
    // blockers since the navigation no longer counts as user-initiated.
    onClickViewAtIdexx = async (e, result) => {
        e.stopPropagation();
        e.preventDefault();
        const popup = window.open('', '_blank');
        try {
            const response = await util.fetchApi(`/api/integrations/idexx/results/${result.id}/view`);
            const url = response?.data?.url;
            if (url) {
                if (popup) popup.location.href = url;
                else window.open(url, '_blank');
            } else {
                if (popup) popup.close();
                // eslint-disable-next-line no-alert
                (await appAlert(response?.message || 'IDEXX did not return a view URL'));
            }
        } catch (err) {
            if (popup) popup.close();
            // eslint-disable-next-line no-alert
            (await appAlert(`Failed to open IDEXX result: ${err.message}`));
        }
    }

    getStatusBadgeClass(status) {
        switch (status) {
            case 'final': return 'bg-success';
            case 'partial': return 'bg-warning text-dark';
            case 'pending': return 'bg-secondary';
            case 'corrected': return 'bg-info';
            case 'cancelled': return 'bg-danger';
            default: return 'bg-secondary';
        }
    }

    render() {
        const { labResults, loading, expandedResult } = this.state;
        const { saleIsLocked = true, classNames = 'mx-4' } = this.props;

        return (<>
            {loading && <div class={`d-flex justify-content-center my-2 ${classNames}`}>
                <div class='spinner-border spinner-border-sm text-primary' role='status'>
                    <span class='visually-hidden'>Loading...</span>
                </div>
            </div>}

            {(labResults && labResults.length > 0) && <>
                {labResults.map(result => (
                    <div
                        class={`bg-body ${classNames} d-flex flex-column box-container mt-2 p-3 lab-result font-style-narrow`}
                        onClick={this.onClickToggleExpand}
                        data-id={`${result.id}`}
                    >
                        <div class='d-flex flex-row justify-content-between align-items-center'>
                            <div class='flex-fill'>
                                <span class='fw-bold'>{result.testName || result.testCode || 'Lab result'}</span>
                                <span class={`badge ${this.getStatusBadgeClass(result.status)} ms-2`}>
                                    {result.status || 'pending'}
                                </span>
                                {result.isAbnormal && <span class='badge bg-warning text-dark ms-1'>ABNORMAL</span>}
                                {result.isCritical && <span class='badge bg-danger ms-1'>CRITICAL</span>}
                            </div>
                            <div class='text-body-secondary'>
                                <small>
                                    {result.resultDate && util.formatDate(result.resultDate, {
                                        hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO',
                                    })}
                                </small>
                            </div>
                        </div>

                        {result.modality && <div class='text-body-secondary mt-1'>
                            <small>{result.modality}</small>
                        </div>}

                        {expandedResult === `${result.id}` && <>
                            {result.interpretation && <div class='mt-2 p-2 bg-body-secondary rounded'>
                                <small class='text-body-secondary'>Interpretation:</small>
                                <div style='white-space: pre-wrap;'>{decodeIdexxText(result.interpretation)}</div>
                            </div>}

                            {result.categories && result.categories.length > 0 && <>
                                {result.categories.map(category => (
                                    <div class='mt-2'>
                                        <small class='text-body-secondary fw-bold'>{category.name}</small>
                                        <table class='table table-sm table-striped mt-1'>
                                            <thead>
                                                <tr>
                                                    <th>Test</th>
                                                    <th>Result</th>
                                                    <th>Unit</th>
                                                    <th>Reference</th>
                                                    <th>Flag</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {category.tests && category.tests.map(test => {
                                                    // IDEXX field names: result (string, may
                                                    // contain '\n' for NOTES type), units, and
                                                    // outOfRange (boolean). We derive a H/L/CH/CL
                                                    // flag from the numeric result + thresholds.
                                                    const flag = deriveTestFlag(test);
                                                    const rowClass = flag
                                                        ? (flag.critical ? 'table-danger' : 'table-warning')
                                                        : '';
                                                    return (
                                                        <tr class={rowClass}>
                                                            <td>{test.name}</td>
                                                            <td style='white-space: pre-wrap;'>{test.result}</td>
                                                            <td><small>{test.units}</small></td>
                                                            <td><small>{test.referenceRange}</small></td>
                                                            <td>{flag && (
                                                                <span class={`badge ${flag.critical ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                                                    {flag.code}
                                                                </span>
                                                            )}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                ))}
                            </>}

                            {result.results && result.results.length > 0 && !result.categories?.length && <>
                                <table class='table table-sm table-striped mt-2'>
                                    <thead>
                                        <tr>
                                            <th>Test</th>
                                            <th>Result</th>
                                            <th>Unit</th>
                                            <th>Reference</th>
                                            <th>Flag</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.results.map(test => {
                                            const flag = deriveTestFlag(test);
                                            const rowClass = flag
                                                ? (flag.critical ? 'table-danger' : 'table-warning')
                                                : '';
                                            return (
                                                <tr class={rowClass}>
                                                    <td>{test.name}</td>
                                                    <td style='white-space: pre-wrap;'>{test.result}</td>
                                                    <td><small>{test.units}</small></td>
                                                    <td><small>{test.referenceRange}</small></td>
                                                    <td>{flag && (
                                                        <span class={`badge ${flag.critical ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                                            {flag.code}
                                                        </span>
                                                    )}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </>}

                            <div class='mt-2 d-flex flex-wrap gap-2'>
                                {result.hasPdf && (
                                    <button
                                        class='btn btn-sm btn-outline-primary rounded-pill'
                                        onClick={(e) => this.onClickViewPdf(e, result)}
                                    >
                                        <i class='fa-solid fa-file-pdf me-1' />{' '}
                                        <Text id='dyrejournal.idexx.viewPdf'>View PDF</Text>
                                    </button>
                                )}
                                <button
                                    class='btn btn-sm btn-outline-secondary rounded-pill'
                                    onClick={(e) => this.onClickViewAtIdexx(e, result)}
                                >
                                    <i class='fa-solid fa-arrow-up-right-from-square me-1' />{' '}
                                    <Text id='dyrejournal.idexx.viewAtIdexx'>View at IDEXX</Text>
                                </button>
                            </div>
                        </>}
                    </div>
                ))}
            </>}

            {!loading && !(labResults && labResults.length > 0) && <>
                <div class={`d-flex flex-column justify-content-center w-100 my-1 text-body-secondary ${classNames}`}>
                    <div class='w-100 text-center' style='font-size: 1.5em;'>
                        <i class='fa-solid fa-flask-vial' />
                    </div>
                    <div class='w-100 text-center' style='font-size: 1.0em;'>
                        {this.props.journal?.id
                            ? 'No lab results for this journal'
                            : 'No lab results for this animal'}
                    </div>
                </div>
            </>}

            {!saleIsLocked && <div class={`d-flex flex-row justify-content-center mt-3 px-3 ${classNames}`}>
                <button
                    class='btn btn-sm btn-outline-primary rounded-pill'
                    onClick={this.onClickFetchNew}
                    disabled={loading}
                >
                    <i class={`fa-solid ${loading ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`} />
                    Fetch new results
                </button>
            </div>}
        </>);
    }
}

export default LabResultsListMini;
